/// <amd-module name="Repos/PullRequestActions/Components/PullRequestCommitChangesPanel" />
import * as React from "react";
import { IChange, IFileDiffParameters } from "Repos/ChangesCommon/Contracts";
import { ChangeSummaryCard } from "Repos/Changes/Components/ChangeSummaryCard";
import { ILocalFileChange } from "Repos/Changes/Contracts";
import { GitPush, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { getFileName, getIconNameForFile, getParentPath } from "Repos/Common/Util/File";
import { getInvalidBranchNameMessage } from "Repos/Common/Util/Ref";
import * as Resources from "Repos/PullRequestActions/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IRegionComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Intersection } from "VSSUI/Intersection";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";

export interface IPullRequestCommitChangesDetails {
    branch: string;
    comment: string;
}

export interface ICommitChangesContext {
    addPushCompleteListener: (listener: PushCompleteListener) => void;
}

export declare type PushCompleteListener = (push: GitPush, newBranchName?: string) => void | Promise<void>;

export interface IPullRequestCommitChangesPanelProps {
    branchName: string;
    changes: ILocalFileChange[];
    changeVersionString: string;
    comment: string;
    commitChangesContext: ICommitChangesContext;
    errorMessage: IReadonlyObservableValue<string>;
    showOverlay: IReadonlyObservableValue<boolean>;
    onDismiss: (details?: IPullRequestCommitChangesDetails) => void;
}

export class PullRequestCommitChangesPanel extends VssComponent<IPullRequestCommitChangesPanelProps> {
    private comment: ObservableValue<string>;
    private branchName: ObservableValue<string>;
    private fieldsValid: ObservableValue<boolean>;

    private commentError = new ObservableValue(false);
    private commentErrorMessage = new ObservableValue("");

    private branchError = new ObservableValue(false);
    private branchErrorMessage = new ObservableValue("");

    constructor(props: IPullRequestCommitChangesPanelProps) {
        super(props);

        this.comment = new ObservableValue(props.comment);
        this.branchName = new ObservableValue(props.branchName);

        this.fieldsValid = new ObservableValue(!!this.branchName.value && !!this.comment.value);
    }

    public render() {
        return (
            <Observer valid={this.fieldsValid} showOverlay={this.props.showOverlay}>
                {(props: { valid: boolean; showOverlay: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => {
                                onDismiss();
                            },
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: Resources.Commit
                        }
                    ];
                    return (
                        <Panel
                            defaultActiveElement={".repos-commit-panel-comment-input"}
                            lightDismiss={false}
                            onDismiss={() => {
                                onDismiss();
                            }}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.Commit }}
                            footerButtonProps={buttons}
                        >
                            <Intersection>
                                <div className="flex-grow flex-column rhythm-vertical-16 v-scroll-auto custom-scrollbar scroll-auto-hide padding-horizontal-4">
                                    <Observer errorMessage={this.props.errorMessage}>
                                        {(props: { errorMessage: string }) => {
                                            return (
                                                props.errorMessage && (
                                                    <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                                )
                                            );
                                        }}
                                    </Observer>
                                    <FormItem label={Resources.Comment} error={this.commentError} message={this.commentErrorMessage}>
                                        <TextField
                                            onChange={this.onCommentChanged}
                                            multiline={true}
                                            value={this.comment}
                                            inputClassName="repos-commit-panel-comment-input"
                                            rows={5}
                                        />
                                    </FormItem>
                                    <FormItem label={Resources.BranchName} error={this.branchError} message={this.branchErrorMessage}>
                                        <TextField onChange={this.onBranchNameChanged} value={this.branchName} />
                                    </FormItem>
                                    <WrappedComponent<IRegionComponentProperties>
                                        dependencies={["ms.vss-code-web.commit-changes-panel-extensions"]}
                                        wrappedType="ms.vss-web.region"
                                        wrappedProps={{
                                            componentRegion: "commitChangesPanelExtensions",
                                            childComponentProperties: { commitChangesContext: this.props.commitChangesContext }
                                        }}
                                    />
                                    <FormItem
                                        label={Resources.Changes}
                                        className="repos-commit-panel-changes repos-changes-viewer rhythm-vertical-16"
                                    >
                                        {this.props.changes.map(change => {
                                            return (
                                                <ChangeSummaryCard
                                                    change={this.localChangeToIChange(change)}
                                                    getDiffParameters={() => this.getDiffParameters(change)}
                                                    key={change.path}
                                                />
                                            );
                                        })}
                                    </FormItem>
                                </div>
                            </Intersection>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        const details = {
            branch: this.branchName.value,
            comment: this.comment.value
        };

        this.props.onDismiss(details);
    };

    private onCommentChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, comment: string): void => {
        this.comment.value = comment;

        let valid = true;
        if (!comment) {
            this.commentErrorMessage.value = Resources.CommentCannotBeEmpty;
            valid = false;
        } else {
            this.commentErrorMessage.value = "";
        }

        this.commentError.value = !valid;

        this.validate();
    };

    private onBranchNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, branchName: string): void => {
        this.branchName.value = branchName;

        this.branchErrorMessage.value = getInvalidBranchNameMessage(branchName) || "";
        this.branchError.value = !!this.branchErrorMessage.value;

        this.validate();
    };

    private validate(): void {
        const valid = !this.commentError.value && !this.branchError.value && !!this.comment.value;
        if (valid !== this.fieldsValid.value) {
            this.fieldsValid.value = valid;
        }
    }

    private localChangeToIChange(change: ILocalFileChange): IChange {
        return {
            changeType: VersionControlChangeType.Edit,
            commitId: "",
            folder: getParentPath(change.path)!,
            iconProps: { iconName: getIconNameForFile(change.path) },
            isFolder: false,
            isInvalid: false,
            objectId: "",
            path: change.path,
            name: getFileName(change.path),
            sourcePath: change.path
        };
    }

    private getDiffParameters(change: ILocalFileChange): IFileDiffParameters {
        return {
            includeCharDiffs: true,
            modifiedPath: change.path,
            modifiedContent: change.getText().value,
            modifiedVersion: this.props.changeVersionString,
            originalPath: change.path,
            originalContent: change.getOriginalText().value,
            originalVersion: this.props.changeVersionString,
            partialDiff: true
        };
    }
}
